/**
 * ChallengeSessionV2 - Main page for v2 Challenge Taking UI
 *
 * Modern, accessible challenge-taking experience with:
 * - Full keyboard navigation
 * - Focus mode for distraction-free testing
 * - Smooth animations and transitions
 * - Equal priority mobile/desktop responsive design
 * - Test Mode countdown with auto-submit and time warnings
 */

import { useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import {
  useChallengeV2 as useChallengeStoreV2,
  selectIsLoading,
  selectHasError,
  selectError,
  selectCurrentQuestion,
  selectIsAdaptiveStage,
  selectCountdownWarningLevel,
  selectCountdownExpired,
  selectIsCountdownMode
} from '../hooks/useChallenge'

// Layout components
import ChallengeContainerV2 from '../components/v2/ChallengeContainerV2'
import ChallengeHeaderV2 from '../components/v2/ChallengeHeaderV2'

// Question components
import QuestionCardV2 from '../components/v2/question/QuestionCardV2'

// Modals
import ReviewModalV2 from '../components/v2/modals/ReviewModalV2'
import StageCompleteModalV2 from '../components/v2/modals/StageCompleteModalV2'
import ChallengeCompleteModalV2 from '../components/v2/modals/ChallengeCompleteModalV2'
import UnansweredWarningV2 from '../components/v2/modals/UnansweredWarningV2'
import FinalAnswerConfirmationV2 from '../components/v2/modals/FinalAnswerConfirmationV2'
import PauseOverlayV2 from '../components/v2/modals/PauseOverlayV2'

// Hooks
import { useKeyboardNavigationV2 } from '../hooks/v2/useKeyboardNavigationV2'

// Focus mode wrapper
import FocusModeWrapperV2 from '../components/v2/layout/FocusModeWrapperV2'

// Keyboard hints
import KeyboardHintsV2 from '../components/v2/layout/KeyboardHintsV2'

// Warning banner labels and styles by countdown warning level
const WARNING_CONFIG = {
  three: {
    label: '3 minutes remaining',
    classes: 'bg-amber-50 border-amber-300 text-amber-800'
  },
  two: {
    label: '2 minutes remaining',
    classes: 'bg-orange-50 border-orange-300 text-orange-800'
  },
  one: {
    label: '1 minute remaining',
    classes: 'bg-red-50 border-red-300 text-red-800 animate-pulse'
  }
}

const CountdownWarningBanner = () => {
  const warningLevel = useChallengeStoreV2(selectCountdownWarningLevel)

  if (!warningLevel) return null

  const config = WARNING_CONFIG[warningLevel]
  if (!config) return null

  return (
    <div
      className={`fixed left-0 right-0 lg:left-64 z-50 border-b px-4 py-2 text-center text-sm font-semibold ${config.classes}`}
      style={{ top: '120px' }}
    >
      {config.label}
    </div>
  )
}

const ChallengeSessionV2 = () => {
  const navigate = useNavigate()
  const { challengeId } = useParams()
  const autoSubmitTriggered = useRef(false)

  // Store state
  const isLoading = useChallengeStoreV2(selectIsLoading)
  const hasError = useChallengeStoreV2(selectHasError)
  const error = useChallengeStoreV2(selectError)
  const currentQuestion = useChallengeStoreV2(selectCurrentQuestion)
  const isAdaptive = useChallengeStoreV2(selectIsAdaptiveStage)
  const isFocusMode = useChallengeStoreV2((state) => state.isFocusMode)

  // Countdown state
  const countdownExpired = useChallengeStoreV2(selectCountdownExpired)
  const isCountdownMode = useChallengeStoreV2(selectIsCountdownMode)

  // Store actions
  const initializeChallenge = useChallengeStoreV2((state) => state.initializeChallenge)
  const resetAll = useChallengeStoreV2((state) => state.resetAll)
  const handleSubmitStage = useChallengeStoreV2((state) => state.handleSubmitStage)
  const initializeFinalAnswerWarning = useChallengeStoreV2(
    (state) => state.initializeFinalAnswerWarning
  )

  // Sync the "don't show the final-answer confirmation again" preference from localStorage on mount.
  useEffect(() => {
    initializeFinalAnswerWarning()
  }, [initializeFinalAnswerWarning])

  // Initialize keyboard navigation
  useKeyboardNavigationV2()

  // Initialize challenge on mount
  useEffect(() => {
    const init = async () => {
      if (!challengeId) {
        toast.error('No challenge ID provided')
        navigate('/dashboard')
        return
      }

      const result = await initializeChallenge(challengeId)
      if (!result.success) {
        // If challenge is completed, redirect to report page
        if (result.isCompleted) {
          navigate(`/challenge/${challengeId}/report`, { replace: true })
          return
        }
        toast.error(result.error || 'Failed to load challenge')
        navigate('/dashboard')
      }
    }

    init()

    // Cleanup on unmount
    return () => {
      resetAll()
    }
  }, [challengeId, initializeChallenge, resetAll, navigate])

  // Auto-submit when countdown expires (Test Mode)
  useEffect(() => {
    if (countdownExpired && isCountdownMode && !autoSubmitTriggered.current) {
      autoSubmitTriggered.current = true
      toast('Time expired. Submitting your answers...', { icon: '\u23F0' })
      handleSubmitStage(true) // force=true bypasses unanswered check
    }

    // Reset the ref when countdown is no longer expired (new stage started)
    if (!countdownExpired) {
      autoSubmitTriggered.current = false
    }
  }, [countdownExpired, isCountdownMode, handleSubmitStage])

  // Handle errors
  useEffect(() => {
    if (hasError && error) {
      toast.error(error)
    }
  }, [hasError, error])

  // Loading state
  if (isLoading && !currentQuestion) {
    return (
      <div className="force-light-mode min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-indigo-500 border-t-transparent"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading challenge...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (hasError && !currentQuestion) {
    return (
      <div className="force-light-mode min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to Load Challenge</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    )
  }

  return (
    <FocusModeWrapperV2 isActive={isFocusMode}>
      {/* Main content - forced light mode */}
      <div className="force-light-mode h-full min-h-0 bg-gray-50 flex flex-col select-none">
        {/* Unified Header with Navigation - Fixed position */}
        <ChallengeHeaderV2 />

        {/* Countdown warning banner (Test Mode) */}
        <CountdownWarningBanner />

        {/* Spacer for fixed header (h-14 = 56px) + mobile progress bar (h-1) */}
        <div className="h-[60px] sm:h-14 flex-shrink-0" />

        {/* Main Content */}
        <ChallengeContainerV2>
          <QuestionCardV2 />
        </ChallengeContainerV2>

        <KeyboardHintsV2 />
      </div>

      {/* Modals - respect site theme */}
      <ReviewModalV2 />
      <StageCompleteModalV2 />
      <ChallengeCompleteModalV2 />
      <UnansweredWarningV2 />
      <FinalAnswerConfirmationV2 />
      <PauseOverlayV2 />
    </FocusModeWrapperV2>
  )
}

export default ChallengeSessionV2
